import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Added import
import { IncludedTranscripts } from '@components/interview/analysis-components/IncludeTranscripts';
import { Metadata } from '@components/interview/analysis-components/MetaData';
import { PriorityInsights } from '@components/interview/analysis-components/ProrityInsight';
import { Summary } from '@components/interview/analysis-components/Summary';
import AutoStoriesIcon from '@mui/icons-material/AutoStories'; // Example Icon for Narrative Arc
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import NumbersIcon from '@mui/icons-material/Numbers';
import SyncAltIcon from '@mui/icons-material/SyncAlt'; // Added for Emotional Journey
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useParams } from 'react-router';
import { AnalysisLayout } from './layout';

/** -------------------------------------------------------------
 * NarrativeArcAnalysis – displays a portfolio-level Narrative Arc analysis
 * ------------------------------------------------------------*/
export default function NarrativeArcAnalysis({ item }) {
  const [expanded, setExpanded] = React.useState(false);
  const { id } = useParams();
  if (!item) return null;

  // Attempt to get the results object
  let results = {};
  if (typeof item.results === 'object' && item.results !== null) {
    results = item.results; // Use directly if already an object
  } else if (typeof item.results === 'string') {
    try {
      results = JSON.parse(item.results); // Try parsing if it's a string
    } catch (err) {
      console.error('Failed to parse results JSON string:', err, 'JSON String:', item.results);
      results = {}; // Assign empty object on parsing error
    }
  } else {
    // Handle cases where results is neither object nor string (e.g., undefined, null)

    console.warn('Results field is not an object or string:', item.results);
  }

  // Fallback if results are invalid or missing the required 'summary'
  if (Object.keys(results).length === 0 || !results.summary) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" color="error" gutterBottom>
            Analysis Data Unavailable
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The results for this Narrative Arc analysis could not be loaded or are improperly
            formatted.
          </Typography>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Show Raw Data</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {JSON.stringify(item, null, 2)}
              </pre>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
    );
  }

  /* ──────────────────────── Arc Prevalence Accordions ─────────────────────── */
  const ArcPrevalence = () => {
    if (!results.arc_prevalence?.length > 0) return null;

    const handleChange = (panel) => (event, isExpanded) => {
      setExpanded(isExpanded ? panel : false);
    };

    return (
      <Box>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Narrative Arc Breakdown
        </Typography>
        {results.arc_prevalence.map((arc, index) => {
          const panelId = `arc-panel-${index}`;
          return (
            <Accordion
              key={panelId}
              expanded={expanded === panelId}
              onChange={handleChange(panelId)}
              sx={{ mb: 1 }}
              variant="outlined"
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${panelId}-content`}
                id={`${panelId}-header`}
                sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 2 } }}
              >
                <Typography variant="subtitle1" sx={{ flexShrink: 2, fontWeight: 500 }}>
                  {arc.arc_label}
                </Typography>
                <Chip
                  icon={<NumbersIcon />}
                  label={`Prevalence: ${arc.prevalence}`}
                  size="small"
                  variant="outlined"
                  sx={{ ml: 'auto' }}
                />
              </AccordionSummary>
              <AccordionDetails sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                {/* Common Emotional Journey */}
                {arc.common_emotional_journey?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <SyncAltIcon fontSize="small" color="secondary" /> Common Emotional Journey(s)
                    </Typography>
                    <List dense disablePadding>
                      {arc.common_emotional_journey.map((journey, i) => (
                        <ListItem key={i} disableGutters sx={{ pl: 1 }}>
                          <ListItemText
                            primary={journey}
                            primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Representative Quotes */}
                {arc.representative_quotes?.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <FormatQuoteIcon fontSize="small" color="action" /> Representative Quotes
                    </Typography>
                    <List dense disablePadding sx={{ pl: 1 }}>
                      {arc.representative_quotes.map((quote, i) => (
                        <ListItem key={i} disableGutters>
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <FormatQuoteIcon
                              sx={{ fontSize: 16, transform: 'scaleX(-1)', color: 'text.disabled' }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={quote}
                            primaryTypographyProps={{
                              variant: 'body2',
                              fontStyle: 'italic',
                              color: 'text.secondary',
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  /* ──────────────────────────── Render ─────────────────────────── */
  return (
    <AnalysisLayout results={results} item={{ ...item, name: 'Narrative Arc Analysis' }}>
      <CardHeader
        title={
          <Typography
            variant="h5"
            gutterBottom
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <AutoStoriesIcon sx={{ verticalAlign: 'bottom', mr: 1 }} />
            Narrative Arc Analysis
            {item.analysis_lens_type && (
              <AnalysisLensHelperText lensName={item.analysis_lens_type} level="run" size="small" />
            )}
          </Typography>
        }
      />
      <Divider />
      <CardContent>
        <Metadata item={item} />
        <Summary results={results} item={item} />
        <PriorityInsights results={results} />
        <ArcPrevalence />
        <IncludedTranscripts item={item} id={id} />
      </CardContent>
    </AnalysisLayout>
  );
}

NarrativeArcAnalysis.propTypes = {
  item: PropTypes.object.isRequired,
};
